// app/_layout.tsx
import UpdateManager from "@/components/default/UpdateManager";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { Stack, useRouter, useSegments } from "expo-router";
import { useEffect, useState } from "react";
import { ActivityIndicator, useColorScheme, View } from "react-native";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { Provider, useDispatch, useSelector } from "react-redux";
import { RootState, store } from "../store";
import { restoreSession } from "../store/slices/authSlice";
import { setTheme } from "../store/slices/themeSlice";

// Theme loader component
function ThemeLoader({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch();
  const systemColorScheme = useColorScheme();

  useEffect(() => {
    const loadTheme = async () => {
      const savedTheme = await AsyncStorage.getItem("app-theme");
      if (savedTheme === "light" || savedTheme === "dark") {
        dispatch(setTheme(savedTheme));
      } else {
        const systemTheme = systemColorScheme === "dark" ? "dark" : "light";
        dispatch(setTheme(systemTheme));
        await AsyncStorage.setItem("app-theme", systemTheme);
      }
    };
    loadTheme();
  }, [dispatch, systemColorScheme]);

  return children;
}

// Auth guard component
function AuthGuard({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const segments = useSegments();
  const { isAuthenticated, loading } = useSelector(
    (state: RootState) => state.auth,
  );
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      await store.dispatch(restoreSession());
      setIsReady(true);
    };
    initAuth();
  }, []);

  useEffect(() => {
    if (!isReady) return;

    const inAuthGroup = segments[0] === "(auth)";
    const inAppGroup = segments[0] === "(app)";

    if (!isAuthenticated && !inAuthGroup) {
      router.replace("/(auth)/login");
    } else if (isAuthenticated && !inAppGroup && !inAuthGroup) {
      router.replace("/(app)/dashboard");
    } else if (isAuthenticated && inAuthGroup) {
      router.replace("/(app)/dashboard");
    }
  }, [isAuthenticated, segments, isReady]);

  if (!isReady || loading) {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator size="large" color="#3b82f6" />
      </View>
    );
  }

  return children;
}

export default function RootLayout() {
  return (
    <Provider store={store}>
      <ThemeLoader>
        <SafeAreaProvider>
          <AuthGuard>
            <UpdateManager />
            <Stack screenOptions={{ headerShown: false }} />
          </AuthGuard>
        </SafeAreaProvider>
      </ThemeLoader>
    </Provider>
  );
}
